import i18n from '@/plugins/i18n'
import { getI18n } from '@/plugins/i18n'

import { useLookup, useDefaultDialogTitle } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'

import { useGarageStore } from '@/stores/garage.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useValidation } from '@/composables/validation'
import { useSubjectService } from '@/services/subject.service'
import { useDocumentService } from '@/services/document.service'

export const useDialogTitle = () => {
  const { t } = i18n.global
  const { defaultActionTitle } = useDefaultDialogTitle()
  const registryStore = useRegistryStore()

  const itemTitle = (i) => {
    const customer = registryStore.customers.find(c => c.id === i.customer_id) || null
    return i ? `${i.number_mnemonic}/${i.series} ${customer ? customer?.name : ''}` : ''
  }

  const dialogTitle = (i) => {
    const title = `${defaultActionTitle(i)} ${t('common.quote')}`
    return i.id ? `${title} - ${itemTitle(i)}` : title
  }

  return { dialogTitle, itemTitle }
}

export const useColumns = (
  editors,
  currentCountryId = {},
  currentStateId = {},
  currentCustomerTypeId = {},
  currentCustomerSubTypeId = {},
  currentVehicleFuelType = {},
  formInstance = {},
  isChangeOwnerDisabled = {},
  includesVat = {},
  selectedInterventionTypeId = {},
  currentCustomerVatCountryId = {},
  currentCustomerVatCountryCode = {},
  currentCarService = {},
  currentInfoBikeBrandCode = {},
  currentInfoBikeModelCode = {},
  setCellValueZipCode,
  setCellValueCity
) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const webSiteStore = useWebSiteStore()
  const garageStore = useGarageStore()
  const { setSubjectType, isVatCountryIta, isNameExtMandatory } = useSubjectService()
  const { validateTaxCode } = useValidation()
  const { setVehicleNextRevisionDueDate, setIncludesVat } = useDocumentService()

  const { withDocumentSeriesAndNumberMnemonicUpdate: _withUpdate } = dataGridColumn.useDocumentSeriesAndNumberMnemonicUpdate()
  const trackedDocumentIdFields = ['deposit', 'garage_reason_ref', 'vehicle']
  const withDocumentSeriesAndNumberMnemonicUpdate = (fn) =>
    _withUpdate(fn, trackedDocumentIdFields, editors, garageStore.api.getQuoteNextIdByDataAsync)

  const toggleReadOnlyValidation = (field) => ({
    type: 'custom',
    message: i18n.t('common.required'),
    reevaluate: true,
    validationCallback: (e) => {
      const value = e.data.garage_reason_ref
      const currentGarageReason = garageStore.garageReasons.find(i => i.id === value)

      const interventionType = garageStore.interventionTypes.find(i => i.id === currentGarageReason?.intervention_type_ref)

      if (interventionType?.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE) {
        return e.value != null && e.value !== ''
      }

      return true
    }
  })

  const getSubjectTypes = (options) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', null],
      sort: 'name',
    }
  }

  const getSubjectSubTypes = (option) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', option.data?.customer?.subject_type_ref || 0],
      sort: 'name',
    }
  }

  const setChangeOwnerDisabled = (data) => {
    isChangeOwnerDisabled.value = (data.customer_id === data.vehicle?.customer_id || !data.vehicle_id)
  }

  return [
    {
      type: 'buttons',
      buttons: [{template: 'editButtonsTemplate'}],
      width: '6rem',
      visibleIndex: 0,
      fixed: false
    },
    dataGridColumn.useAttachmentColumn(),
    dataGridColumn.useDocumentDateColumn({validationRules: [{ type: 'required' }]}),
    dataGridColumn.useSeriesColumn(),
    dataGridColumn.useNumberMnemonicColumn(),
    dataGridColumn.useReferenceDocumentColumn({
      dataField: 'worksheet.id',
      caption: i18n.t('common.generatedDocumentRef'),
      editCellTemplate: 'worksheetRef',
      cellTemplate: 'worksheetRef',
    }),
    { dataField: 'customer_name_name_ext_vat', caption: i18n.t('common.customer'), visible: false, showInColumnChooser: false, editable: false },
    dataGridColumn.useCustomerColumn({
      dataField: 'customer_id',
      editCellTemplate: 'customerEditor',
      setCellValue: async(newData, value, currentData) => {
        dataGridColumn.useCustomerColumn().setCellValue(newData, value)
        newData.customer = value || { ...webSiteStore.emptyCustomer }
        newData.customer_id = newData?.customer?.id
        if (currentCountryId) {
          currentCountryId.value = value?.address?.country
        }
        if (currentStateId) {
          currentStateId.value = value?.address?.state
        }
        if (!currentData.rows_count) {
          newData.vat_type_ref = newData.customer.vat_type_ref
        }
        const currentCustomer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === value?.id)
        setIncludesVat(newData, value, currentData, includesVat, currentCustomer)
        newData.payment_type_ref = currentCustomer?.accounting_payment_type_ref
        newData.currency_ref = currentCustomer?.accounting_currency_ref || webSiteStore.getDefaultCurrency()?.id
        if (value) {
          setSubjectType(currentCustomer, currentData, currentCustomer.subject_type_ref, currentCustomer.subject_sub_type_ref)
        }
        currentCountryId.value = currentCustomer?.address?.country
        currentStateId.value = currentCustomer?.address?.state
        setChangeOwnerDisabled({ ...currentData, ...newData })
      },
    }),
    {
      dataField: 'customer.subject_type_ref',
      caption: i18n.t('common.personType'),
      lookup: useLookup({ dataSource: getSubjectTypes }),
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        currentCustomerTypeId.value = value
        currentCustomerSubTypeId.value = null
        setSubjectType(newData.customer, currentData, value)
        editors['customer.vat_number'].option('disabled', !isVatCountryIta(currentData.customer?.vat_country_id))
      },
      validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.subject_sub_type_ref',
      caption: `${i18n.t('common.category')} / ${i18n.t('common.gender')}`,
      lookup: useLookup({ dataSource: getSubjectSubTypes }),
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        currentCustomerSubTypeId.value = value
        setSubjectType(newData.customer, currentData, currentData.customer?.subject_type_ref, value)
      },
      validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useCustomerAddressCountryColumn({ setCellValue: (newData, value, currentData) => dataGridColumn.useAddressRef().setCellValueCountry(newData, value, currentData) }),
    dataGridColumn.useCustomerAddressStateColumn({ setCellValue: (newData, value, currentData) => dataGridColumn.useAddressRef().setCellValueState(newData, value, currentData) }),
    dataGridColumn.useCustomerAddressCityColumn({ setCellValue: (newData, value, currentData) => setCellValueCity(newData, value, currentData) }),
    { dataField: 'customer.code', visible: false, showInColumnChooser: false, validationRules: [{ type: 'required' }], caption: `${i18n.t('common.customer')} ${i18n.t('common.code')}`},
    { dataField: 'customer.name', visible: false, showInColumnChooser: false, validationRules: [{ type: 'required' }], caption: `${i18n.t('common.customer')} ${i18n.t('common.nameAlt')}`},
    {
      dataField: 'customer.name_ext',
      visible: false,
      showInColumnChooser: false,
      caption: `${i18n.t('common.customer')} ${i18n.t('common.nameExt')}`,
      validationRules: [{
        type: 'custom',
        reevaluate: true,
        message: i18n.t('common.required'),
        validationCallback: (e) => !isNameExtMandatory(e.data?.customer?.subject_type_ref) || !!e.value,
      }],
    },
    {
      caption: `${i18n.t('common.customer')} ${i18n.t('common.vatCountry')}`,
      dataField: 'customer.vat_country_id',
      editCellTemplate: 'vatCountryEditor',
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        newData.customer.vat_country_id = value?.id
        editors['customer.vat_number'].option('disabled', !isVatCountryIta(newData.customer?.vat_country_id))
      },
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useCustomerVatNumberColumn({ vatCountryCode: currentCustomerVatCountryCode.value, vatCountryId: currentCustomerVatCountryId.value, subjectSubTypeId: currentCustomerSubTypeId.value }),
    dataGridColumn.useTaxCodeColumn(
      {
        dataField: 'customer.tax_code',
        caption: `${i18n.t('common.customer')} ${i18n.t('common.taxCode')}`,
        validationRules: [{
          type: 'custom',
          reevaluate: true,
          validationCallback: (e) => !registryStore.companyData.subject_validate_vat_and_fiscal_code || validateTaxCode(e.value, currentCustomerVatCountryCode.value),
        }],
        visible: false,
        showInColumnChooser: false,
      }
    ),
    { dataField: 'customer.address.country_name', visible: false, showInColumnChooser: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.country')}`},
    { dataField: 'customer.address.state_name', visible: false, showInColumnChooser: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.state')}`},
    { dataField: 'customer.address.city_name', visible: false, showInColumnChooser: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.city')}`},
    dataGridColumn.useCustomerAddressZipCodeColumn({setCellValueZipCode: (newData, value, currentData, type) => setCellValueZipCode(newData, value, currentData, 'customer', false)}),
    dataGridColumn.useCustomerAddressStreetColumn(),
    dataGridColumn.useEmailColumn({ dataField: 'customer.email', caption: `${i18n.t('common.customer')} ${i18n.t('common.email')}`}),
    dataGridColumn.useCustomerMobileColumn(),
    { dataField: 'customer.phone', visible: false, showInColumnChooser: false, caption: `${i18n.t('common.customer')} ${i18n.t('common.phone')}`},
    {
      dataField: 'garage',
      caption: i18n.t('common.garage'),
      editCellTemplate: 'depositEditor',
      lookup: useLookup({ store: registryStore.getLocalizedDeposits() }),
      setCellValue: (newData, value) => {
        newData.garage = value?.id
      },
      visible: false,
      showInColumnChooser: false,
      validationRules: [{ type: 'required' }],
    },
    dataGridColumn.useDepositColumn({
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate((newData, value) => { newData.deposit = value?.id }),
    }),
    {
      dataField: 'garage_reason_ref',
      caption: i18n.t('common.quoteReason'),
      lookup: useLookup({ store: garageStore.getLocalizedGarageReasons() }),
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate(async(newData, value, currentData) => {
        newData.garage_reason_ref = value
        const currentGarageReason = garageStore.garageReasons.find(i => i.id === value)

        const interventionType = garageStore.interventionTypes.find(i => i.id === currentGarageReason?.intervention_type_ref)
        selectedInterventionTypeId.value = interventionType?.id
        const hasCarService = interventionType?.has_car_service
        editors['car_service']?.option('disabled', !hasCarService)

        const isCarService = interventionType?.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE
        if (!hasCarService || !isCarService) {
          newData.car_service = null
          return
        }

        if (currentCarService.value) {
          newData.car_service = currentCarService.value
          return
        }

        if (currentData?.vehicle?.id) {
          const res = await registryStore.api.getVehicleNextCarServiceAsync(currentData.vehicle.id)
          currentCarService.value = newData.car_service = res.data
          return
        }
      }),
      validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.carService'),
      dataField: 'car_service',
      dataType: 'number',
      editCellTemplate: 'carServiceNumberEditor',
      visible: false,
      showInColumnChooser: false,
      validationRules: [toggleReadOnlyValidation('car_service')],
    },
    dataGridColumn.usePaymentTypeColumn({ visible: false, showInColumnChooser: false }),
    dataGridColumn.useDiscountColumn({ visible: false, showInColumnChooser: false }),
    dataGridColumn.useTotalNoVatColumn(),
    dataGridColumn.useTotalVatColumn(),
    dataGridColumn.useCurrencyColumn({ visible: false, showInColumnChooser: false, editors }),
    dataGridColumn.useExchangeRateColumn({ visible: false, showInColumnChooser: false }),
    dataGridColumn.useExchangeRateDateColumn({ visible: false, showInColumnChooser: false }),
    dataGridColumn.usepaidColumn({
      caption: webSiteStore.blankCaption,
      dataField: 'worksheet.paid',
      editCellTemplate: 'paidCellTemplate',
      visible: false,
      showInColumnChooser: false,
    }),
    {
      caption: i18n.t('common.tag', 2),
      dataField: 'tags',
      editCellTemplate: 'tagsEditor',
      cellTemplate: 'tagsEditor',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useNotesColumn({ visible: false, showInColumnChooser: false }),
    dataGridColumn.usepaidAmountColumn({
      caption: i18n.t('common.paidAmount'),
      dataField: 'worksheet.paid_amount',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
    }),
    {
      caption: i18n.t('common.priceVat', 2),
      dataField: 'includes_vat',
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false,
      setCellValue: (newData, value) => {
        newData.includes_vat = includesVat.value = value
      },
    },
    {
      caption: i18n.t('common.mechanic'),
      dataField: 'mechanics',
      cellTemplate: 'mechanicsAvatarList',
      editCellTemplate: 'mechanicsEditor',
      lookup: useLookup({
        store: webSiteStore.userDetails.filter(i => i.is_mechanic),
        sort: 'name',
        displayExpr: (item) => `${item.name} ${item.name_ext}`,
      }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.expectedDeliveryDate'),
      dataField: 'expected_delivery_date',
      dataType: 'date',
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'infoBike.plate_number',
      caption: ' ',
      editCellTemplate: 'infoBikePlateNumberEditor',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useInfoBikeBrandColumn(
      {
        visible: false,
        showInColumnChooser: false
      }
    ),
    dataGridColumn.useInfoBikeModelColumn({
      brand_code: currentInfoBikeBrandCode,
      visible: false,
      showInColumnChooser: false
    }),
    dataGridColumn.useInfoBikeVariantColumn({
      model_code: currentInfoBikeModelCode,
      visible: false,
      showInColumnChooser: false
    }),
    {
      dataField: 'infoBike.infoBike',
      caption: i18n.t('common.infoBike'),
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useVehicleColumn({
      caption: i18n.t('common.vehicle'),
      dataField: 'vehicle_id',
      editCellTemplate: 'vehicleEditor',
      formInstance: formInstance,
      setCellValue: async(newData, value, currentData) => {
        dataGridColumn.useVehicleColumn().setCellValue(newData, value)
        if (value?.preserveOwner) {
          newData.customer = value.preservedCustomer ?? currentData.customer
          newData.customer_id = value.preservedCustomerId ?? currentData.customer_id
        }
        if (value?.id) {
          newData.vehicle = (await registryStore.getOrFetchVehiclesAsync()).find(i => i.id === value.id)
        } else {
          newData.vehicle = { ...webSiteStore.emptyVehicle }
          if (!value?.preserveOwner) {
            newData.customer = { ...webSiteStore.emptyCustomer }
            newData.customer_id = null
          }
        }
        newData.vehicle_id = newData?.vehicle?.id

        if (value?.customer_id && !value?.preserveOwner) {
          newData.customer = {}
          newData.customer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === value.customer_id)
          if (newData.customer) {
            newData.customer_id = newData.customer.id
            newData.customer.vat_type_ref = newData.customer.accounting_vat_type_ref
          }
        }

        if (newData.vehicle?.id) {
          const res = await registryStore.api.getVehicleNextCarServiceAsync(newData.vehicle.id)
          currentCarService.value = res.data
          const garageReasonRef = currentData.garage_reason_ref
          const currentGarageReason = garageStore.garageReasons.find(i => i.id === garageReasonRef)

          const interventionType = garageStore.interventionTypes.find(i => i.id === currentGarageReason?.intervention_type_ref)
          if (interventionType?.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE) {
            newData.car_service = res.data
          }
        }

        setIncludesVat(newData, value, currentData, includesVat, newData.customer)

        setVehicleNextRevisionDueDate(newData)

        currentVehicleFuelType.value = newData.vehicle?.fuel_type ?? null
        setChangeOwnerDisabled({ ...currentData, ...newData })
      },
    }),
    dataGridColumn.useVatTypeColumn({ dataField: 'vat_type_ref', caption: i18n.t('common.accountingVat'), visible: false, showInColumnChooser: false, validationRules: [] }),
    dataGridColumn.useSupplierBrandColumn({
      dataField: 'vehicle.supplier_brand_ref',
      all: true,
      visible: false,
      showInColumnChooser: false,
      validationRules: [{ type: 'required' }],
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate((newData, value) => {
        if (!newData.vehicle) newData.vehicle = {}
        newData.vehicle.supplier_brand_ref = value
      }),
    }),
    {
      dataField: 'vehicle.infoBikeCodeFull',
      caption: i18n.t('common.infoBike'),
      visible: false,
      showInColumnChooser: false,
      editorOptions: {
        readOnly: true,
      },
    },
    {
      dataField: 'vehicle.name',
      caption: i18n.t('common.description'),
      visible: false,
      showInColumnChooser: false,
      validationRules: [{ type: 'required' }]
    },
    {
      dataField: 'vehicle.color',
      caption: i18n.t('common.color'),
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.usePlateNumberColumn(),
    dataGridColumn.useVinColumn({ dataField: 'vehicle.vin', visible: false }),
    dataGridColumn.useEngineSizeColumn({ dataField: 'vehicle.engine_size' }),
    {
      caption: i18n.t('common.engineCode'),
      dataField: 'vehicle.engine_code',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useVehicleKmColumn(),
    {
      caption: i18n.t('common.enrollmentDate'),
      dataField: 'vehicle.enrollment_date',
      dataType: 'date',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.saleDate'),
      dataField: 'vehicle.sale_date',
      dataType: 'date',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.revisionDueDate'),
      dataField: 'vehicle.revision_due_date',
      dataType: 'date',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useNextRevisionDueDateColumn(),
  ].filter(i => i.dataField !== 'worksheet.id' || webSiteStore.hasClaim(webSiteStore.claimsEnum.WORKSHOP_WORKSHEET_READ))
}
